<template>
  <div>
    <div class="relative gap-4 lg:flex">
      <div
        class="relative hidden flex-col items-center justify-center gap-3.5 pb-3.5 lg:flex"
        :style="{
          height: `${swiperSize}px`,
        }"
      >
        <div
          :class="
            cn(
              'absolute left-0 top-0 z-10 w-full shrink-0 items-center justify-center bg-white p-2.5 has-[:disabled]:hidden',
              [thumbsSwiper && activeImages.length > 6 ? 'flex' : 'hidden'],
            )
          "
        >
          <button ref="prevEl" :aria-label="$t('previousSlide')">
            <UiIcon name="arrow_up" class="size-6" />
          </button>
        </div>

        <swiper
          :space-between="14"
          :slides-per-view="5"
          direction="vertical"
          :allow-touch-move="true"
          :a11y="true"
          :modules="[Thumbs]"
          watch-slides-progress
          round-lengths
          :breakpoints="{
            [screens.md]: {
              slidesPerView: 5,
            },
            [screens.xl]: {
              slidesPerView: 6,
            },
          }"
          class="configurator-assets-slider h-[calc(100%-58px)]"
          :style="{
            width: `${thumbSize}px`,
            maxWidth: `${thumbSize}px`,
          }"
          @swiper="setThumbsSwiper"
          @resize="setThumbsSwiper"
        >
          <swiper-slide
            v-for="(image, idx) in activeImages"
            :key="idx"
            class="aspect-1 border border-transparent"
          >
            <iframe
              v-if="image.type === assetsTypes.v360"
              :src="format360Url(image.url)?.mobile || ''"
              width="100%"
              height="100%"
              class="d3-diamond pointer-events-none"
              frameborder="0"
              @click.stop
            />
            <video
              v-else-if="image.type === assetsTypes.video"
              :src="image.url"
              autoplay
              loop
              muted
              playsinline
            />
            <UiLazyImg
              v-else
              :src="image.url"
              :alt="
                image.alt || productData?.currentTranslation?.title || 'image'
              "
            />
          </swiper-slide>
        </swiper>
        <div
          class="flex w-full shrink-0 items-center justify-center bg-white p-2.5"
        >
          <button
            v-show="thumbsSwiper && activeImages.length > 6"
            ref="nextEl"
            :aria-label="$t('nextSlide')"
          >
            <UiIcon name="arrow_down" class="size-6" />
          </button>
        </div>
      </div>
      <swiper
        class="!mx-0 aspect-1 min-w-0 flex-auto"
        :space-between="10"
        :slides-per-view="1"
        :navigation="navigation"
        :allow-touch-move="true"
        :preload-images="true"
        :auto-height="false"
        :update-on-images-ready="true"
        :a11y="true"
        round-lengths
        :modules="modules"
        :thumbs="{ swiper: thumbsSwiper }"
        :pagination="{
          enabled: true,
          clickable: true,
        }"
        :breakpoints="{
          [screens.md]: {
            pagination: {
              enabled: true,
              clickable: true,
            },
          },
          [screens.lg]: {
            pagination: {
              enabled: false,
              clickable: false,
            },
          },
        }"
        @swiper="setSwiper"
        @resize="setSwiper"
        @slide-change="onSlideChange"
      >
        <swiper-slide
          v-for="(image, idx) in activeImages"
          :key="idx"
          class="!flex items-center justify-center max-md:items-start"
        >
          <div class="flex aspect-1 w-full items-center justify-center">
            <iframe
              v-if="image.type === assetsTypes.v360"
              :src="format360Url(image.url)?.desktop || ''"
              width="100%"
              height="100%"
              class="d3-diamond"
              scrolling="no"
              frameborder="0"
            ></iframe>
            <video
              v-else-if="image.type === assetsTypes.video"
              :src="image.url"
              autoplay
              loop
              muted
              playsinline
              class="w-full"
            ></video>
            <template v-else>
              <ClientOnly v-if="mdAndUp">
                <vue-photo-zoom-pro
                  class="max-w-full"
                  :width="160"
                  :high-url="image.url"
                  :selector="mdAndUp"
                >
                  <UiLazyImg
                    :src="image.url"
                    :alt="
                      image.alt ||
                      productData?.currentTranslation?.title ||
                      'image'
                    "
                    class="h-full max-w-full"
                    data-e2e-image="product"
                  />
                </vue-photo-zoom-pro>
              </ClientOnly>
              <UiLazyImg
                v-else
                :src="image.url"
                :alt="
                  image.alt || productData?.currentTranslation?.title || 'image'
                "
                class="max-w-full"
                data-e2e-image="product"
              />
            </template>
          </div>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Navigation,
  Thumbs,
  Pagination,
  Swiper as SwiperInstance,
} from "swiper";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";
import { NavigationOptions } from "swiper/types";
import vuePhotoZoomPro from "vue-photo-zoom-pro";
import { ProductData } from "ecom-types";
import "vue-photo-zoom-pro/dist/style/vue-photo-zoom-pro.css";
import { screens } from "~/configs/breakpointsConfig.json";

interface ImageData {
  alt?: string;
  url: string;
  type?: string;
}
const props = defineProps<{
  images: ImageData[];
  showArrows: boolean;
  productData?: ProductData;
  productImages?: ImageData[];
  diamondImages?: ImageData[];
  isDiamond?: boolean;
  hideThumbnail?: boolean;
}>();

const emit = defineEmits<{
  "slide-change": [activeIndex: number];
}>();

const { mdAndUp } = useAppBreakpoints();

enum ActiveImgOptions {
  product = "product",
  diamond = "diamond",
}

enum assetsTypes {
  v360 = "v360",
  video = "video",
}

const modules = [Navigation, Thumbs, Pagination];
const activeImageType = ref(
  props.isDiamond ? ActiveImgOptions.diamond : ActiveImgOptions.product,
);

const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

const navigation = reactive({
  prevEl,
  nextEl,
  disabledClass: "opacity-50 cursor-not-allowed hover:bg-transparent",
}) as NavigationOptions;

watchEffect(() => {
  if (prevEl.value && nextEl.value) {
    navigation.prevEl = prevEl.value;
    navigation.nextEl = nextEl.value;
  }
});

const thumbsSwiper = ref<typeof SwiperInstance | null>(null);
const swiperIns = ref<typeof SwiperInstance | null>(null);
const thumbSize = ref(88);
const swiperSize = ref(650);

async function setSwiper(swiper: typeof SwiperInstance) {
  swiperIns.value = swiper;
  await nextTick();
  swiperSize.value = swiper?.slidesSizesGrid?.[0];
}

async function setThumbsSwiper(swiper: typeof SwiperInstance) {
  thumbsSwiper.value = swiper;
  await nextTick();
  thumbSize.value = swiper?.slidesSizesGrid?.[0];
}

const activeImages = computed<ImageData[]>(() => {
  const images =
    props.productImages?.length && props.diamondImages?.length
      ? activeImageType.value === ActiveImgOptions.product
        ? props.productImages
        : props.diamondImages
      : props.images;

  if (
    (props.productData?.view360_url || props.productData?.movies?.[0]?.url) &&
    activeImageType.value === ActiveImgOptions.diamond
  ) {
    const assetObj = { type: "", url: "" };
    if (props.productData?.view360_url) {
      assetObj.url = props.productData.view360_url || "";
      assetObj.type = assetsTypes.v360;
    } else if (props.productData?.movies?.[0]?.url) {
      assetObj.url = props.productData?.movies?.[0]?.url || "";
      assetObj.type = assetsTypes.video;
    }
    return props.isDiamond ? [assetObj, ...images] : [...images, assetObj];
  }
  return images;
});

const activeImageThumbnail = computed(() => {
  if (activeImageType.value === ActiveImgOptions.product) {
    return {
      image: props.diamondImages?.[0],
      type: ActiveImgOptions.diamond,
    };
  } else {
    return {
      image: props.productImages?.[0],
      type: ActiveImgOptions.product,
    };
  }
});

async function changeActiveImages() {
  activeImageType.value =
    activeImageType.value === ActiveImgOptions.product
      ? ActiveImgOptions.diamond
      : ActiveImgOptions.product;

  await nextTick();

  if (!swiperIns.value?.destroyed) {
    swiperIns.value?.slideTo(0);
  }
  if (!thumbsSwiper.value?.destroyed) {
    thumbsSwiper.value?.slideTo(0);
  }
}

function format360Url(view360_url: string) {
  if (!props.productData?.view360_url) {
    return null;
  }
  const urlDesktop = new URL(view360_url);
  const urlMobile = new URL(view360_url);
  const searchParamsDesktop = [
    ["btn", "0"],
    ["v", "6"],
    ["zoomslide", "1"],
    ["z", "1"],
    ["isBorderRadius", "0"],
    ["sr", "100"],
  ];
  const searchParamsMobile = [
    ["btn", "0"],
    ["v", "6"],
    ["zoomslide", "0"],
    ["z", "0"],
    ["isBorderRadius", "0"],
  ];
  searchParamsDesktop.forEach((el) => {
    urlDesktop.searchParams.set(el[0], el[1]);
  });
  searchParamsMobile.forEach((el) => {
    urlMobile.searchParams.set(el[0], el[1]);
  });
  return { desktop: urlDesktop.href, mobile: urlMobile.href };
}

function onSlideChange(swiper: typeof SwiperInstance) {
  emit("slide-change", swiper.activeIndex);
}
</script>

<style scoped>
.configurator-assets-slider {
  @apply mx-0 flex-auto flex-shrink-0;
}
.configurator-assets-slider :deep(.swiper-slide-thumb-active) {
  @apply border-orange-60;
}
.configurator-assets-slider :deep(.swiper-pagination-bullet-active) {
  background-color: #000;
}
.swiper :deep(.swiper-pagination) {
  @apply absolute bottom-2;
}
.swiper :deep(.swiper-pagination-bullet.swiper-pagination-bullet-active) {
  @apply relative w-6 bg-primary;
}
.swiper :deep(.swiper-pagination-bullet) {
  @apply mx-2 size-2.5 rounded-md bg-gray-30;
}
.swiper
  :deep(.swiper-pagination-bullet.swiper-pagination-bullet-active)::after {
  content: "";
  border-radius: 10px;
  display: block;
  width: 30px;
  height: 16px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  @apply border border-primary;
}
.vue-photo-zoom-pro :deep(.selector) {
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
}
@media (hover: none) {
  .vue-photo-zoom-pro :deep(.selector) {
    display: none;
  }
}
</style>
